import { useCallback, useEffect, useState } from 'react';
import { Loader2, Shield, Trash2, UserPlus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import type {
  MarketplaceMemberRole,
  MarketplaceTeamMember,
} from '@/hooks/supabase/useMarketplaceMemberships';

interface TeamTabProps {
  product: MapleMarketApp;
  canManageTeam: boolean;
  listMembers: (productId: string) => Promise<MarketplaceTeamMember[]>;
  addMemberByEmail: (productId: string, emailOrUserId: string, role: MarketplaceMemberRole) => Promise<void>;
  updateMemberRole: (membershipId: string, role: MarketplaceMemberRole) => Promise<void>;
  removeMember: (membershipId: string) => Promise<void>;
}

const roles: MarketplaceMemberRole[] = ['owner', 'admin', 'developer', 'moderator'];

export default function TeamTab({
  product,
  canManageTeam,
  listMembers,
  addMemberByEmail,
  updateMemberRole,
  removeMember,
}: TeamTabProps) {
  const { toast } = useToast();
  const [members, setMembers] = useState<MarketplaceTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteValue, setInviteValue] = useState('');
  const [inviteRole, setInviteRole] = useState<MarketplaceMemberRole>('developer');
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    setLoading(true);

    try {
      const nextMembers = await listMembers(product.id);
      setMembers(nextMembers);
    } catch (error) {
      toast({
        title: 'Unable to load team',
        description: error instanceof Error ? error.message : 'Could not fetch team members.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [listMembers, product.id, toast]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const handleAddMember = async () => {
    if (!inviteValue.trim()) {
      toast({
        title: 'Member required',
        description: 'Enter an email address or user ID to add a teammate.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      await addMemberByEmail(product.id, inviteValue, inviteRole);
      setInviteValue('');
      await loadMembers();
      toast({
        title: 'Member added',
        description: 'The product team has been updated.',
      });
    } catch (error) {
      toast({
        title: 'Could not add member',
        description: error instanceof Error ? error.message : 'Unable to add that member.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (membershipId: string, role: MarketplaceMemberRole) => {
    setUpdatingId(membershipId);

    try {
      await updateMemberRole(membershipId, role);
      await loadMembers();
      toast({
        title: 'Role updated',
        description: 'Team permissions were saved.',
      });
    } catch (error) {
      toast({
        title: 'Role update failed',
        description: error instanceof Error ? error.message : 'Unable to update member role.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRemove = async (membershipId: string) => {
    setUpdatingId(membershipId);

    try {
      await removeMember(membershipId);
      await loadMembers();
      toast({
        title: 'Member removed',
        description: 'The product team has been updated.',
      });
    } catch (error) {
      toast({
        title: 'Remove failed',
        description: error instanceof Error ? error.message : 'Unable to remove this member.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Team</h3>
        <p className="text-sm text-muted-foreground">Owners and admins can invite teammates and change per-product roles.</p>
      </div>

      {canManageTeam ? (
        <div className="grid gap-4 rounded-xl border border-border p-4 lg:grid-cols-[1fr_180px_auto] lg:items-end">
          <div className="space-y-2">
            <Label htmlFor="team-member">Email or user ID</Label>
            <Input
              id="team-member"
              value={inviteValue}
              onChange={(event) => setInviteValue(event.target.value)}
              placeholder="user@example.com or UUID"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as MarketplaceMemberRole)}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" onClick={() => void handleAddMember()} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Add member
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          You have read-only access to this team. Only site admins, product owners, and product admins can change roles.
        </div>
      )}

      <div className="space-y-3">
        {members.map((member) => {
          const displayName =
            member.profile?.full_name || member.profile?.username || member.profile?.email || member.user_id;
          const isUpdating = updatingId === member.id;

          return (
            <div key={member.id} className="flex flex-col gap-4 rounded-xl border border-border p-4 lg:flex-row lg:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Shield className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{displayName}</div>
                  <div className="truncate text-sm text-muted-foreground">{member.profile?.email || member.user_id}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {canManageTeam ? (
                  <Select value={member.role} onValueChange={(value) => void handleRoleChange(member.id, value as MarketplaceMemberRole)} disabled={isUpdating}>
                    <SelectTrigger className="w-[160px] capitalize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role} className="capitalize">
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className="capitalize">{member.role}</Badge>
                )}

                {canManageTeam && (
                  <Button type="button" variant="outline" size="icon" disabled={isUpdating} onClick={() => void handleRemove(member.id)}>
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {!loading && members.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No team members were found for this product.
          </div>
        )}

        {loading && (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Loading team members…
          </div>
        )}
      </div>
    </div>
  );
}
